/**
 * @fileoverview WebSocket lifecycle, reconnect, and activity detection for Chat.
 * @module pages/chat/useChatSocket
 */
import { useCallback, useEffect, useRef } from "react";
import { logger } from "../../utils/logger.js";
import {
  initializeWebSocket,
  fetchActiveConnections,
} from "../../logic/chat/index.js";
import { dispatchChatWebSocketMessage } from "../../logic/chat/websocketHandlers.js";
import { handleWebSocketReconnect } from "./websocketReconnect.js";
import {
  REFRESH_INTERVAL_ACTIVE_CHAT_MS,
  REFRESH_INTERVAL_ACTIVE_USER_MS,
  REFRESH_INTERVAL_INACTIVE_USER_MS,
  USER_INACTIVITY_THRESHOLD_MS,
  INACTIVITY_CHECK_INTERVAL_MS,
} from "../../utils/chatUiConstants.js";

/**
 * Manages WebSocket connection, reconnect backoff, and user activity tracking.
 * @param {Object} ctx - Chat context from `useChatContext`
 * @returns {{ connect: Function }}
 */
const useChatSocket = (ctx) => {
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const connectRef = useRef(null);

  connectRef.current = () => {
    const c = ctxRef.current;
    const handlerDeps = {
      setChatMessages: c.setChatMessages,
      setChats: c.setChats,
      setIsFileUploading: c.setIsFileUploading,
      navigate: c.navigate,
      isUserActiveRef: c.isUserActiveRef,
      lastSeenMessageTimestampByChatRef: c.lastSeenMessageTimestampByChatRef,
      pendingFileUploads: c.pendingFileUploads,
      selectedChatRef: c.selectedChatRef,
    };

    const onOpen = () => {
      c.setIsReconnecting(false);
      c.reconnectAttemptsRef.current = 0;
    };

    const onMessage = (event) =>
      dispatchChatWebSocketMessage(event, handlerDeps);

    const onClose = (_event) => {
      handleWebSocketReconnect(c, () => connectRef.current?.());
    };

    c.ws.current = initializeWebSocket(onMessage, onOpen, null, onClose);
  };

  const connect = useCallback(() => {
    connectRef.current?.();
  }, []);

  const disconnect = useCallback(() => {
    const c = ctxRef.current;
    if (c.ws.current) {
      logger.info("Closing WebSocket connection");
      c.ws.current.close();
      c.ws.current = null;
    }
    if (c.reconnectTimeoutRef.current) {
      clearTimeout(c.reconnectTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => disconnect();
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [disconnect]);

  useEffect(() => {
    if (!ctx.allowed) {
      return () => {};
    }

    let interval;
    const startRefresh = () => {
      if (interval) {
        clearInterval(interval);
      }

      let refreshInterval;
      if (ctx.selectedChat && ctx.isUserActive) {
        refreshInterval = REFRESH_INTERVAL_ACTIVE_CHAT_MS;
      } else if (ctx.isUserActive) {
        refreshInterval = REFRESH_INTERVAL_ACTIVE_USER_MS;
      } else {
        refreshInterval = REFRESH_INTERVAL_INACTIVE_USER_MS;
      }

      interval = setInterval(() => {
        if (ctx.ws.current?.readyState === WebSocket.OPEN) {
          fetchActiveConnections(ctx.navigate, ctx.setActiveConnections);
        }
      }, refreshInterval);
    };

    startRefresh();
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [ctx.allowed, ctx.selectedChat, ctx.isUserActive]);

  useEffect(() => {
    const updateActivity = () => {
      const now = Date.now();
      if (!ctx.isUserActive) {
        ctx.lastActivityRef.current = now;
        ctx.setIsUserActive(true);
        ctx.isUserActiveRef.current = true;
        if (ctx.ws.current?.readyState === WebSocket.CLOSED) {
          ctx.reconnectAttemptsRef.current = 0;
          connect();
        }
      } else {
        ctx.lastActivityRef.current = now;
      }
    };

    const checkInactivity = () => {
      const timeSinceLastActivity = Date.now() - ctx.lastActivityRef.current;
      if (
        timeSinceLastActivity > USER_INACTIVITY_THRESHOLD_MS &&
        ctx.isUserActive
      ) {
        ctx.setIsUserActive(false);
        ctx.isUserActiveRef.current = false;
      }
    };

    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    const inactivityInterval = setInterval(
      checkInactivity,
      INACTIVITY_CHECK_INTERVAL_MS,
    );

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
      clearInterval(inactivityInterval);
    };
  }, [ctx.isUserActive, connect]);

  return { connect, disconnect };
};

export default useChatSocket;
